import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, Card, EmptyState, Loading, PageHeader, Table, Td } from '../components/ui/primitives';
import { EngagementPanel } from '../components/analytics/EngagementPanel';
import { ProviderTable } from '../components/deliverability/ProviderTable';
import { dateTime, gradeColour, relative } from '../lib/format';
import type { Alert, DomainHealth, EngagementReport, ProviderRow } from '../lib/types';

interface Dashboard {
  engagement: EngagementReport;
  providers: ProviderRow[];
  alerts: Alert[];
  domains: DomainHealth[];
  activeListings: number;
  integrations: Array<{ kind: string; provider: string; enabled: boolean; status: string }>;
}

export function DashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<Dashboard>('/api/analytics/dashboard?days=30'),
  });

  const { data: timeline } = useQuery({
    queryKey: ['timeline', 30],
    queryFn: () => api.get<{ series: never[] }>('/api/analytics/timeline?days=30'),
  });

  if (isLoading || !data) return <Loading label="Loading dashboard" />;

  return (
    <>
      <PageHeader
        title="Deliverability overview"
        description="Last 30 days across every client. Engagement figures are human-only; the unfiltered number sits beneath each one."
      />

      <div className="space-y-6">
        <EngagementPanel report={data.engagement} timeline={timeline?.series} />

        <div className="grid gap-5 lg:grid-cols-3">
          <Card
            title="Open alerts"
            subtitle="Blocklist listings, reputation drops, Microsoft blocks and circuit-breaker trips."
            className="lg:col-span-2"
          >
            {data.alerts.length === 0 ? (
              <EmptyState title="Nothing needs attention" body="No open alerts across any sending domain." />
            ) : (
              <ul className="space-y-2.5">
                {data.alerts.slice(0, 8).map((alert) => (
                  <li
                    key={alert.id}
                    className="rounded-lg border border-ink-200 px-4 py-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge
                            tone={
                              alert.severity === 'critical' ? 'bad' : alert.severity === 'warning' ? 'warn' : 'info'
                            }
                          >
                            {alert.severity}
                          </Badge>
                          <span className="text-sm font-medium text-ink-900">{alert.title}</span>
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-ink-500">{alert.body}</p>
                      </div>
                      <span className="shrink-0 text-xs text-ink-400">{relative(alert.createdAt)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Sending domains" subtitle="Reputation score and current state.">
            {data.domains.length === 0 ? (
              <EmptyState title="No sending domains yet" />
            ) : (
              <ul className="space-y-2">
                {data.domains.map((domain) => (
                  <li key={domain.id}>
                    <Link
                      to={'/domains/' + domain.id}
                      className="flex items-center justify-between rounded-lg border border-ink-200 px-3.5 py-2.5 hover:bg-ink-50"
                    >
                      <div>
                        <div className="font-mono text-xs font-medium text-ink-900">{domain.domain}</div>
                        <div className="mt-0.5 flex items-center gap-1.5">
                          <Badge tone={domain.status === 'active' ? 'good' : domain.status === 'quarantined' ? 'bad' : 'neutral'}>
                            {domain.status.replace(/_/g, ' ')}
                          </Badge>
                          <Badge>{domain.exclusivity}</Badge>
                        </div>
                      </div>
                      <span
                        className={
                          'rounded-md border px-2 py-1 text-sm font-semibold tabular-nums ' +
                          gradeColour(domain.grade)
                        }
                      >
                        {domain.score === null ? '--' : domain.score.toFixed(0)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <Card
          title="Delivery by receiving provider"
          subtitle="Microsoft-hosted recipients are separated out, including Microsoft 365 business domains identified by MX."
        >
          <ProviderTable rows={data.providers} />
        </Card>

        <Card title="Connected integrations" subtitle="Verification, warmup, reputation feeds, placement and analytics.">
          <Table head={['Type', 'Provider', 'Enabled', 'Status']}>
            {data.integrations.map((i) => (
              <tr key={i.kind + i.provider}>
                <Td className="capitalize">{i.kind}</Td>
                <Td className="font-medium text-ink-900">{i.provider}</Td>
                <Td>{i.enabled ? <Badge tone="good">on</Badge> : <Badge>off</Badge>}</Td>
                <Td className="text-xs text-ink-500">{i.status}</Td>
              </tr>
            ))}
          </Table>
        </Card>

        <p className="text-xs text-ink-400">Generated {dateTime(new Date())}</p>
      </div>
    </>
  );
}
